import { createContext, useContext, useEffect, useState } from 'react'
import RoutesManagment from '../Routes/RoutesManagment';
import { logIn, ResetPassword, UsersForChangePassword, ChangePassword, LogOut } from '../Services/AuthenticationService';
import { CartProvider } from './CartProvider';
import { CompanyInfoProvider } from './CompanyInfoProvider';
import { FavoriteItemsProvider } from './FavoriteProductsProvider';
import LoadingSpinner from '../Components/Components/LoadingSpinner';
import useHandleAuthState from '../Hooks/useHandleAuthState';
import { CustomerProvider } from './CustomerProvider';
import { useConfig } from './ConfigProvider';
import { LastWatchedProductsProvider } from './LastWatchedProductsProvider';
import { InvoicesProvider } from './InvoicesProvider';
import { UpdateUserMarketing } from '../Services/UserService';
import { OffersProvider } from './OffersProvider';
import { SavedBasketsProvider } from './SavedBasketsProvider';
import { UserProvider } from './UserProvider';
import { useParams } from './ParamsProvider';
import { toast } from 'react-toastify';
import { GetCurrentUser, GetUserPassword } from '../Services/AccountService';
import { DiscountProvider } from './DiscountProvider';
import { setAccessToken, getAccessToken } from '../Services/TokenService';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext()

export const useAuth = () => {
    return useContext(AuthContext)
}

const AuthProvider = ({ children }) => {
    const navigate = useNavigate()
    const { api, logOutTrigger } = useConfig()
    const { authState, pending, setAuthState, setRefresh } = useHandleAuthState()
    const { autoLogOutTime } = useParams()
    const [wholesalePrices, setWholesalePrices] = useState(!!authState.user)
    const [loginError, setLoginError] = useState("")

    useEffect(() => {
        setWholesalePrices(!!authState.user)
    }, [authState.user])

    useEffect(() => { //trigger na wylogowanie gdy wygaśnie token
        if (logOutTrigger !== null && authState.user) {
            logout(false, true)
        }
    }, [logOutTrigger])

    useEffect(() => { //timer na automatycznne odświeżenie tokena           
        if (!authState.user || !api) {
            return
        }
        const interval = setInterval(async () => {
            try {
                if (!getAccessToken()) {
                    clearInterval(interval)
                    return
                }

                const response = await api.post("/api/v1/Authentication/refreshToken", {}, { withCredentials: true })

                if (response.data) {
                    setAccessToken(response.data.accessToken)
                }
            } catch (err) {
                var accountIsNoLongerActive = true
                if (err.response.status != 401) {
                    console.error("Błąd przy odświeżaniu tokena:", err)
                    accountIsNoLongerActive = false
                }
                logout(true, true, accountIsNoLongerActive)
            }
        }, autoLogOutTime * 60 * 1000)

        return () => clearInterval(interval)
    }, [authState.user])

    const login = async (data, goToHomePage = true) => {
        setLoginError("")
        try {
            const res = await logIn(api, data)
            if (res.data.errorMessage && res.data.errorMessage.trim() !== '') {
                return { manyCompanies: false, companies: null, errorMessage: res.data.errorMessage }
            }
            if (res.data.companies && res.data.companies.length > 0) {
                return { manyCompanies: true, companies: res.data.companies, errorMessage: res.data.errorMessage }
            }
            else {
                clearLocalStorageData()
                setAccessToken(res.data.accessToken)
                const userRes = await GetCurrentUser(api)
                const newState = {
                    user: userRes.data,
                    custId: userRes.data.custId,
                    siteId: userRes.data.siteId,
                    whId: userRes.data.whId,
                    currency: userRes.data.currency,
                    selectedCustomer: null,
                    selectedCustomerTax: null,
                    selectedCustomerData: null,
                    initialSiteId: userRes.data.siteId,
                    initialWhId: userRes.data.whId,
                }
                setAuthState(newState)

                if (goToHomePage) {
                    navigate('/')
                }
                else {
                    setRefresh(true)
                }
                return { manyCompanies: false, companies: null, errorMessage: res.data.errorMessage }
            }
        } catch (e) {
            console.error(e)
            if (e.response && e.response.status === 401) {
                setLoginError(e.response.data)
            } else {
                setLoginError("Błąd połączenia z serwerem")
            }
        }
    }

    const updateConsentForUser = (consentMarketing, email, sms, phone) => {
        UpdateUserMarketing(api, consentMarketing, email, sms, phone)
            .then(() => {
                setRefresh(true)
            })
            .catch(e => {
                console.error(e)
            })
    }

    const logout = async (goToLoginPage = true, tokenExpired = false) => {
        try {
            await LogOut(api)
        } catch (error) {
            console.log(error)
        }
        logOutClearDataFuncBody()
        const path = window.location.pathname.toLowerCase()
        const search = window.location.search.toLowerCase()

        if (tokenExpired) {
            const noAutoLogoutPaths = [
                "/confirmaccount",
                "/accountcreated",
                "/changepassword",
                "/login"
            ]

            // sprawdzenie: czy jesteśmy na /cart i jednocześnie w query jest custId? Aby blokowac wylogowanie na realizacji oferty ale na Cart już wylogować
            const isCartWithCustId = path === "/cart" && new URLSearchParams(search).has("custId".toLowerCase())
            const cancelAutoLogout = noAutoLogoutPaths.includes(path) || isCartWithCustId

            if (!cancelAutoLogout) {
                navigate("/login?autoLogout")
            }
            return
        }
        if (goToLoginPage) {
            navigate("/login")
        }
    }

    const logOutClearDataFuncBody = () => {
        clearLocalStorageData()
        setAuthState({
            user: null,
            custId: null,
            siteId: null,
            whId: null,
            currency: 'PLN',
            selectedCustomer: null,
            selectedCustomerTax: null,
            selectedCustomerData: null
        })
    }

    const clearLocalStorageData = () => {
        localStorage.removeItem('APP_LICENSE')
        localStorage.removeItem('authState')
        localStorage.removeItem('categoryNavigationTree')
        localStorage.removeItem('indexMask')
        localStorage.removeItem('priceListExportMaxArticle')
        localStorage.removeItem('productDisplayMode')
        localStorage.removeItem('siteId')
        localStorage.removeItem('whId')
        sessionStorage.clear()
        setAccessToken(null)
    }

    const changeSelectedCustomer = (
        custId,
        siteId,
        whId,
        selectedCustomer,
        currency,
        selectedCustomerTax,
        zipCode,
        city,
        street,
        houseNum,
        localNum,
        email,
        phone,
        dpl
    ) => {
        setAuthState(prev => ({
            ...prev,
            custId,
            siteId,
            whId,
            currency: currency || prev.currency,
            selectedCustomer,
            selectedCustomerTax,
            selectedCustomerData: {
                name: selectedCustomer,
                zipCode,
                city,
                street,
                houseNum,
                localNum,
                email,
                phone,
                tax: selectedCustomerTax,
                whId,
                custId,
                siteId,
                dpl
            }
        }))
    }

    const handleSelectCustomer = (customer) => {
        setAuthState(prev => ({
            ...prev,
            selectedCustomerObject: customer
        }))
    }

    const changeSiteIdAndWhId = (siteId, whId) => {
        setAuthState(prev => ({
            ...prev,
            siteId,
            whId
        }))
    }
    const handleResetPassword = async (email) => {
        try {
            const res = await ResetPassword(api, email)
            return res.data
        } catch (error) {
            console.log(error)
            return { resetType: 0, message: 'Wewnętrzny błąd aplikacji!' }
        }
    }
    const handleCheckChangePasswordLink = async (link) => {
        try {
            const res = await UsersForChangePassword(api, link)
            return res.data
        } catch (error) {
            console.log(error)
            return null
        }
    }
    const handleChangePasswordFromLink = async (link, custId, password, marketingAgree, marketingAgreeEmail, marketingAgreePhone, marketingAgreeSms) => {
        try {
            const res = await ChangePassword(api, link, custId, password, marketingAgree, marketingAgreeEmail, marketingAgreePhone, marketingAgreeSms)
            return { result: true, errorMessage: '' }
        } catch (error) {
            console.log(error)
            return { result: false, errorMessage: error.response.data }
        }
    }

    const handleAdmitEInvoiceAgreement = (invoiceEmail) => {
        setAuthState(prev => ({
            ...prev,
            user: {
                ...prev.user,
                customerInfo: prev.user.customerInfo.map((item, index) =>
                    index === 0 ? { ...item, isEInvoiceConsent: true, eInvoiceEmail: invoiceEmail } : item
                )
            }
        }))
    }

    const handleGetUserPassword = async () => {
        try {
            let res = await GetUserPassword(api)
            return res.data
        } catch (e) {
            console.log(e)
            toast.error('Błąd w trakcie pobierania hasła')
            return ''
        }
    }

    if (pending) {
        return (
            <div className='row vh-100 align-items-center' style={{ overflow: 'none', backgroundColor: 'white' }}>
                <LoadingSpinner />
            </div>
        )
    }

    return (
        <AuthContext.Provider value={{
            authState,
            loginError,
            wholesalePrices,
            setWholesalePrices,
            changeSelectedCustomer,
            changeSiteIdAndWhId,
            updateConsentForUser,
            handleResetPassword,
            handleCheckChangePasswordLink,
            handleChangePasswordFromLink,
            logOutClearDataFuncBody,
            handleAdmitEInvoiceAgreement,
            handleSelectCustomer,
            handleGetUserPassword,
            login,
            logout,
            setLoginError,
        }}>
            <DiscountProvider>
                <CompanyInfoProvider>
                    <LastWatchedProductsProvider>
                        <SavedBasketsProvider>
                            <FavoriteItemsProvider>
                                <OffersProvider>
                                    <CartProvider>
                                        <CustomerProvider>
                                            <UserProvider>
                                                <InvoicesProvider>
                                                    <RoutesManagment />
                                                </InvoicesProvider>
                                            </UserProvider>
                                        </CustomerProvider>
                                    </CartProvider>
                                </OffersProvider>
                            </FavoriteItemsProvider>
                        </SavedBasketsProvider>
                    </LastWatchedProductsProvider>
                </CompanyInfoProvider>
            </DiscountProvider>
        </AuthContext.Provider>
    )
}

export default AuthProvider

